import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

/**
 * Reusable FAQ accordion.
 * Props:
 *   items: Array<{ q: string, a: string }>
 *   accentClass?: string  — text color class for the open question (default forest)
 *   testIdPrefix?: string — prefix for data-testid attributes
 */
const FaqAccordion = ({ items = [], accentClass = 'text-terracotta', testIdPrefix = 'faq' }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-accordion`}>
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div
            key={i}
            className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
            data-testid={`${testIdPrefix}-item-${i}`}
          >
            <button
              onClick={() => setOpenIndex(isOpen ? null : i)}
              className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-4 hover:bg-stone-50 transition-colors"
              aria-expanded={isOpen}
              data-testid={`${testIdPrefix}-question-${i}`}
            >
              <span className={`font-heading text-base md:text-lg ${isOpen ? accentClass : 'text-forest'} transition-colors`}>
                {item.q}
              </span>
              <ChevronDown
                size={20}
                className={`flex-shrink-0 text-stone-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <p
                    className="px-5 md:px-6 pb-5 font-body text-stone-600 leading-relaxed text-sm md:text-base"
                    data-testid={`${testIdPrefix}-answer-${i}`}
                  >
                    {item.a}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export default FaqAccordion;
